import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {concatMap, interval} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {NetworkService} from './network.service';

@Injectable({
  providedIn: 'root',
})
export class LoggingService {
  private entries: LogEntry[] = [];

  constructor(
    private readonly http: HttpClient,
    private readonly network: NetworkService,
  ) {
    interval(3000)
      .pipe(
        filter(() => this.network.isOnline$.value && this.entries.length > 0),
        map(() => {
          const count = this.entries.length;
          return this.entries.splice(0, count);
        }),
        concatMap((entries) =>
          this.http.post<void>('/api/logs', entries, {
            headers: {
              'Device-Time': new Date().toISOString(),
            },
          }),
        ),
      )
      .subscribe();
  }

  debug(template: string, properties: { [key: string]: any }): void {
    this.sendLog('debug', template, properties);
  }

  information(template: string, properties: { [key: string]: any }): void {
    this.sendLog('info', template, properties);
  }

  warning(template: string, properties: { [key: string]: any }): void {
    this.sendLog('warning', template, properties);
  }

  error(template: string, properties: { [key: string]: any }): void {
    this.sendLog('error', template, properties);
  }

  critical(template: string, properties: { [key: string]: any }): void {
    this.sendLog('critical', template, properties);
  }

  private sendLog(level: string, template: string, properties: { [key: string]: any }): void {
    const formatted = this.templateString(template, properties);
    console.log(`${level.toLocaleUpperCase()}: ${formatted}`, properties);
    this.entries.push({
      timestamp: Date.now(),
      level,
      message: formatted,
      properties,
    });
  }

  private templateString(template: string, properties: { [key: string]: any }) {
    let output = template;
    Object.keys(properties).forEach((key) => {
      output = output.replace(new RegExp('\\$' + `{${key}}`, 'g'), properties[key]);
    });
    return output;
  }
}

interface LogEntry {
  timestamp: number;
  level: string;
  message: string;
  properties: { [key: string]: any };
}
